import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Container,
  Typography,
  Grid,
  Box,
  TextField,
  useTheme,
  useMediaQuery,
  Paper,
} from "@mui/material";
import TopNavigation from "../TopNavigation";
import "./landingPage.css";

const steps = [
  {
    icon: (
      <img
        src="/L-1.svg"
        alt="Demo SVG"
        style={{
          width: "100%",
          maxWidth: 100,
          height: "30px",
          display: "block",
        }}
      />
    ),
    title: "Upload, Split & Extract",
    description:
      "Upload a file containing your withholding statements, IRS forms, or any other supporting documentation. Our system will start extraction based on your initial suggestions, automatically building an MY structure and a data table for review.",
  },
  {
    icon: (
      <img
        src="/L-2.svg"
        alt="Demo SVG"
        style={{
          width: "100%",
          maxWidth: 100,
          height: "30px",
          display: "block",
        }}
      />
    ),
    title: "Review, Edit, & Approve",
    description:
      "Within the Extraction view, you'll be able to compare & organize the data, make edits, add notes, and other structure changes while simultaneously editing your final file. Organizations can also approve or deny the changes, passing them to a higher level of the team.",
  },
  {
    icon: (
      <img
        src="/L-3.svg"
        alt="Demo SVG"
        style={{
          width: "100%",
          maxWidth: 100,
          height: "30px",
          display: "block",
        }}
      />
    ),
    title: "Data Validation Check",
    description:
      "Once you're happy with the data, address any flagged discrepancies detected by the AI. The Data Validation view provides complete visibility over your extracted data, allowing you to pinpoint exactly which data was extracted and from which files.",
  },
  {
    icon: (
      <img
        src="/L-4.svg"
        alt="Demo SVG"
        style={{
          width: "100%",
          maxWidth: 100,
          height: "30px",
          display: "block",
        }}
      />
    ),
    title: "Export or Push To Admin",
    description:
      "The final step is simple: export your data as an excel or data file or push it directly into Comply Admin. It's really that easy. The process takes just minutes and can handle one ever-growing variety of withholding statements, not standardized with your files alone.",
  },
];

const benefits = [
  {
    title: "Time And Cost Savings",
    description:
      "Fully automate your IRS document processing, freeing up administrators to focus on other tasks. As your dedicated AI learns, it will consistently improve, processing even complex, multi-tiered Withholding Statements in a respectable amount of time.",
  },
  {
    title: "Improved Accuracy And Compliance",
    description:
      "Avoid costly errors with our fully trained AI, continuously updated with the latest IRS rules and regulations. The approval system also ensures your data is regularly reviewed and approved.",
  },
  {
    title: "Stay In Control of Your IRS Documentation",
    description:
      "Gain clarity with a comprehensive audit trail, ensuring complete transparency from start to finish. Our advanced reporting features allow you to export any user data and stay in control of your information.",
  },
];

const manualTimes = [
  { label: "20 - 30 Minutes", desc: "Average time to manually split & upload PDF's packs" },
  { label: "10 - 15 Minutes", desc: "Cursory validation checks" },
  { label: "10 Minutes (Minimum)", desc: "Withholding statement & IRS documentation review" },
  { label: "1 - 2 Hours", desc: "Structure set up & review" },
  { label: "10+ Hours", desc: "Calculate withholding rates & generate reports" },
];

const extractorTimes = [
  {
    label: "Less than 5 Minutes",
    desc: "Upload PDF packs of any IRS form type and Withholding Statement into our system",
  },
  { label: "6 - 20 Seconds", desc: "Split And Extract The Document" },
  { label: "30 Seconds", desc: "Generates valid entity records" },
  {
    label: "10 Minutes",
    desc: "Send the information through the extractor to generate your MY structure into an Excel or XML file",
  },
  { label: "On Demand!", desc: "Calculate withholding rates & produce reports" },
];

const LandingPage = () => {
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [currentImage, setCurrentImage] = useState(0);

  // Refs for scroll and focus functionality
  const demoSectionRef = React.useRef(null);
  const nameInputRef = React.useRef(null);

  const aboutImages = ["/extractor.png", "/extractor1.png", "/extractor2.png", "/extractor3.png"];
  const aboutImageAlts = [
    "Workload File Import screen",
    "Extractor main dashboard",
    "Data Validation in Extraction Review",
    "Reports and Analytics",
  ];
  const aboutImageDescriptions = [
    "Workload File Import: Easily upload and manage your files for extraction and processing.",
    "Extractor: The main dashboard for navigating all compliance and extraction features.",
    "Data Validation: Review and validate extracted data for accuracy and compliance.",
    "Reports: Generate and analyze compliance and operational reports.",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % aboutImages.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleSignIn = () => history.push("/login");
  const handleRegister = () => history.push("/registration");
  const handleLogoClick = () => window.location.reload();

  // Function to scroll to demo section and focus on name input
  const scrollToDemo = () => {
    if (demoSectionRef.current) {
      demoSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      // Focus on name input after scroll animation
      setTimeout(() => {
        if (nameInputRef.current) {
          nameInputRef.current.focus();
        }
      }, 800); // Wait for scroll animation to complete
    }
  };

  // Function to handle video modal
  const handleWatchVideo = () => {
    console.log("Watch Video button clicked");
    try {
      // Using a public demo video as a fallback
      const videoUrl = "https://www.youtube.com/watch?v=dQw4w9WgXcQ";
      window.open(videoUrl, "_blank");
      console.log("Video URL opened:", videoUrl);
    } catch (error) {
      console.error("Error opening video:", error);
      alert(
        "The demo video is currently unavailable. Please try again later or contact support for a personalized demo."
      );
    }
  };

  return (
    <div
      className="landing-page"
      style={{
        width: "100%",
        margin: "0",
        padding: "0",
        display: "flex",
        flexDirection: "column",
        scrollBehavior: "smooth",
      }}
    >
      {/* Top Navigation */}
      <TopNavigation
        onPricingClick={() => {}} // Add pricing handler if needed
        onContactClick={scrollToDemo}
        onSignInClick={handleSignIn}
        onSignUpClick={handleRegister}
        showSignIn={true}
        logoClickHandler={handleLogoClick}
      />

      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #2aa7e0 0%, #1a5276 100%)",
          color: "white",
          py: 8,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container
          maxWidth="100%"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "row",
          }}
        >
          <Grid
            container
            spacing={4}
            alignItems="center"
            justifyContent="center"
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              marginLeft: 20,
              marginRight: 20,
            }}
          >
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "center" },
                justifyContent: "center",
                textAlign: { xs: "center" },
              }}
            >
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{ fontWeight: "bold", fontFamily: "Poppins, sans-serif" }}
              >
                <img
                  src="/L-Logo.svg"
                  alt="Demo SVG"
                  style={{
                    width: "100%",
                    maxWidth: 400,
                    display: "block",
                    borderRadius: 20,
                    margin: "0 auto",
                  }}
                />
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                Powered By Azure AI
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, maxWidth: 420 }}>
                Harness AI technology to automate the extraction of data from Withholding Statements
                and IRS Tax Forms, regardless of their format or complexity.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  mb: 2,
                  justifyContent: { xs: "center", md: "flex-end" },
                }}
              >
                {/* <Button
                  variant="contained"
                  size="large"
                  sx={{
                    color: "#2aa7e0",
                    backgroundColor: "white",
                    borderRadius: 15,
                    fontWeight: 700,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#f0f0f0" },
                  }}
                  onClick={handleRegister}
                >
                  Sign Up For Free
                </Button> */}
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: "#fff",
                    color: "#fff",
                    fontWeight: 700,
                    borderRadius: 15,
                    textTransform: "none",
                    cursor: "pointer",
                    "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.1)" },
                  }}
                  endIcon={
                    <img
                      src="/video.svg"
                      alt="Demo SVG"
                      style={{
                        width: "100%",
                        maxWidth: 100,
                        height: "20px",
                        display: "block",
                      }}
                    />
                  }
                  onClick={handleWatchVideo}
                >
                  Watch Video
                </Button>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",

                alignItems: "center",
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <img
                src="/L-Laptop.svg"
                alt="Demo SVG"
                style={{
                  width: "90%",
                  transform: "scale(1.7)",
                  transformOrigin: "right",
                  height: "auto",
                  display: "block",
                  borderRadius: 20,
                  margin: "0 auto",
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Container maxWidth="600px" sx={{ py: 8 }}>
        <Typography
          variant="h4"
          align="center"
          sx={{
            fontWeight: 800,
            color: "#2aa7e0",
            mb: 6,
            fontSize: { xs: "2rem", md: "2.3rem" },
            letterSpacing: 0.2,
          }}
        >
          How Does Comply Extractor Work?
        </Typography>
        <Grid
          container
          spacing={0}
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "30px",
            flexWrap: "wrap",
          }}
        >
          {steps.map((step, idx) => (
            <Grid
              item
              key={idx}
              sx={{
                flex: "0 1 220px", // Prevents stretching, keeps cards same width
                display: "flex",
                justifyContent: "center",
              }}
            >
              {/* ...existing card code... */}
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {/* Overlapping Icon */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translate(-50%, 0%)",
                    zIndex: 2,
                    bgcolor: "#2aa7e0",
                    width: 55,
                    height: 55,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span style={{ color: "#fff", fontSize: 36 }}>{step.icon}</span>
                </Box>
                {/* Card */}
                <Paper
                  elevation={3}
                  sx={{
                    mt: 4,
                    pt: 4,
                    pb: 1,
                    px: 2,
                    borderRadius: 4,
                    minHeight: 280,
                    maxWidth: 220,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    boxShadow: "0 2px 16px 0 rgba(0,0,0,0.07)",
                    position: "relative",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#757575",
                      mb: 1,
                      mt: 1,
                      textAlign: "center",
                      fontSize: "16px",
                    }}
                  >
                    {step.title}
                  </Typography>
                  {/* Horizontal line under title */}
                  <Box sx={{ width: "80%", mb: 2 }}>
                    <Box sx={{ borderBottom: "2px solid #e0e0e0", width: "100%", mx: "auto" }} />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#757575",
                      textAlign: "center",
                      fontSize: "12px",
                      mt: 1,
                    }}
                  >
                    {step.description}
                  </Typography>
                </Paper>
              </Box>
            </Grid>
          ))}
        </Grid>
        {/* <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <Button
            variant="contained"
            sx={{
              bgcolor: "#22a7e0",
              color: "#fff",
              fontWeight: 700,
              borderRadius: 2,
              px: 2,
              textTransform: "none",
              boxShadow: 2,
              "&:hover": { bgcolor: "#1a5276" },
            }}
            onClick={handleRegister}
          >
            Sign Up Now
          </Button>
        </Box> */}
      </Container>

      {/* Benefits Section */}
      <Box
        maxWidth="100%"
        width="100%"
        sx={{ bgcolor: "#F5F5F5", py: 4, display: "flex", justifyContent: "center" }}
      >
        <Box sx={{ maxWidth: "80%", width: "100%", px: 2 }}>
          <Grid container spacing={4} alignItems="center">
            {/* Left: Heading */}
            <Grid item xs={12} md={5}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: "#2aa7e0",

                  fontSize: { xs: "2rem", md: "2.2rem" },
                  mb: { xs: 4, md: 0 },
                  lineHeight: 1.2,
                }}
              >
                The Benefits Of Withholding
                <br />
                Statement Automation
              </Typography>
            </Grid>
            {/* Right: Benefits List */}
            <Grid item xs={12} md={7}>
              <Grid container direction="column" spacing={3}>
                {benefits.map((benefit, idx) => (
                  <Grid item key={idx}>
                    <Paper
                      elevation={3}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        borderRadius: 3,
                        p: 3,
                        boxShadow: "0 2px 12px 0 rgba(0,0,0,0.07)",
                        background: "#fff",
                      }}
                    >
                      {/* Title */}
                      <Box
                        sx={{
                          minWidth: 200,
                          pr: 2,
                          display: "flex",
                          alignItems: "center",
                          height: "100%",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 400,
                            color: "#757575",
                            fontSize: "14px",
                            whiteSpace: "pre-line",
                            textAlign: "center",
                            width: "100%",
                          }}
                        >
                          {benefit.title}
                        </Typography>
                      </Box>
                      {/* Vertical Divider */}
                      <Box
                        sx={{
                          width: "1.5px",
                          bgcolor: "#e0e0e0",
                          height: "auto",
                          alignSelf: "stretch",
                          mx: 2,
                          borderRadius: 1,
                        }}
                      />
                      {/* Description */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#757575",
                          fontSize: "10px",
                          alignSelf: "center",
                        }}
                      >
                        {benefit.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Time Savings Section */}
      <Container maxWidth="xl" sx={{ py: 8 }}>
        <Typography variant="h4" align="center" sx={{ fontWeight: 700, mb: 6, color: "#2aa7e0" }}>
          How Much Time Could You Save?
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              sx={{ p: 3, bgcolor: "#eaf6fd", height: "400px", width: "420px", marginLeft: "auto" }}
            >
              <Typography variant="h6" sx={{ color: "#2aa7e0", fontWeight: 700, mb: 2 }}>
                Manual Methods
              </Typography>
              {manualTimes.map((item, idx) => (
                <Box key={idx} sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#2aa7e0" }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ fontSize: "14px" }} variant="body2" color="text.secondary">
                    {item.desc}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                bgcolor: "#2aa7e0",
                color: "#fff",
                height: "400px",
                width: "420px",
                marginRight: "auto",
              }}
            >
              <img
                src="/L-Logo.svg"
                alt="Demo SVG"
                style={{
                  width: "100%",
                  maxWidth: 200,
                  marginBottom: "10px",
                  display: "block",
                  borderRadius: 20,
                }}
              />
              {extractorTimes.map((item, idx) => (
                <Box key={idx} sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" color="#fff">
                    {item.desc}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Grid>
        </Grid>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Button
            variant="contained"
            sx={{
              bgcolor: "#2aa7e0",
              color: "#fff",
              fontWeight: 700,
              px: 4,
              borderRadius: 2,
              textTransform: "none",
              "&:hover": { bgcolor: "#1a5276" },
            }}
            onClick={scrollToDemo}
          >
            Schedule a Demo
          </Button>
        </Box>
      </Container>

      {/* Free Extraction Offer */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: 400,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: { xs: 6, md: 10 },
          background: `
            linear-gradient(160deg, rgba(42,167,224,0.7) 0%, rgba(16,120,183,0.7) 100%),
            url('/L-background.svg') center/cover no-repeat
          `,
          color: "#fff",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            maxWidth: "80%",
            width: "100%",
            px: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              textAlign: "left",
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            5 Free Extractions On Us!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 4,
              maxWidth: 500,
              textAlign: "left",
              fontSize: { xs: "1.1rem", md: "1.2rem" },
            }}
          >
            See the power of our solution in action and discover how effortless tax form processing
            can be. Just upload your tax forms or a PDF pack with IRS forms and withholding
            statements, our AI does the heavy lifting. From instantly creating a compliant structure
            to extracting key data from even scanned paper forms, we turn your documentation chaos
            into organized clarity in minutes.
          </Typography>
          {/* <Button
            variant="contained"
            className="free-trial-btn"
            sx={{
              backgroundColor: "#F2AE33",
              color: "#fff",
              fontWeight: 700,
              px: 2,
              borderRadius: 2,
              textTransform: "none",
              boxShadow: 2,
              "&:hover": { bgcolor: "#e6a200" },
            }}
            onClick={() => {
              console.log("Sign Up button clicked with color #F2AE33");
              handleRegister();
            }}
          >
            Sign Up
          </Button> */}
        </Box>
      </Box>

      {/* Demo Section */}
      <Box
        ref={demoSectionRef}
        width="100%"
        sx={{ bgcolor: "#F5F5F5", py: 8, display: "flex", justifyContent: "center" }}
      >
        <Box sx={{ maxWidth: "80%", width: "100%", px: 2 }}>
          <Grid container spacing={6} alignItems="stretch" justifyContent="end" marginLeft="5px">
            <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: "#2aa7e0",
                  width: "400px",
                  mb: 2,
                  fontSize: "28px",
                  lineHeight: 1.1,
                }}
              >
                Effortless IRS Form &<br />
                Withholding Statement
                <br />
                Extraction
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#555", mb: 4, fontSize: "13px", width: "400px" }}
              >
                Get a firsthand look at how our Comply Extractor product can benefit your
                Withholding Statement processing and management. Alternatively, see for yourself by
                signing up for our free trial.
              </Typography>
              {/* <Paper
                elevation={2}
                sx={{
                  borderRadius: 5,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 2,
                  bgcolor: '#fafbfc',
                  height: 400,
                  flex: 1,
                }}
              >
                <Box sx={{ p: 0 }}> */}
              <img
                src="/L-Final.svg"
                alt="Demo SVG"
                style={{
                  width: "100%",
                  maxWidth: 400,
                  display: "block",
                  borderRadius: 20,
                }}
              />
              {/* </Box>
              </Paper> */}
            </Grid>

            <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "column" }}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  borderRadius: 4,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  width: "100%",
                  maxWidth: 420,
                  alignItems: "center",
                  mx: "auto",
                  bgcolor: "#fff",
                  height: 400,
                  flex: 1,
                }}
              >
                <Typography
                  variant="h5"
                  align="center"
                  sx={{
                    fontWeight: 700,
                    color: "#2aa7e0",
                    mb: 3,
                    fontSize: "32px",
                  }}
                >
                  Schedule A Demo
                </Typography>
                <Box
                  component="form"
                  sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}
                >
                  <TextField
                    label="Name"
                    variant="outlined"
                    fullWidth
                    size="small"
                    InputProps={{
                      sx: { bgcolor: "#e3f3fd", borderRadius: 2 },
                    }}
                    ref={nameInputRef}
                  />
                  <TextField
                    label="Email"
                    variant="outlined"
                    fullWidth
                    size="small"
                    InputProps={{
                      sx: { bgcolor: "#e3f3fd", borderRadius: 2 },
                    }}
                  />
                  <TextField
                    label="Company"
                    variant="outlined"
                    fullWidth
                    size="small"
                    InputProps={{
                      sx: { bgcolor: "#e3f3fd", borderRadius: 2 },
                    }}
                  />
                  <TextField
                    label="Message"
                    variant="outlined"
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    InputProps={{
                      sx: { bgcolor: "#e3f3fd", borderRadius: 2 },
                    }}
                  />
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button
                      variant="contained"
                      sx={{
                        bgcolor: "#22a7e0",
                        color: "#fff",
                        fontWeight: 700,
                        height: 38,
                        width: 150,
                        borderRadius: 2,
                        mt: 1,
                        textTransform: "none",
                        fontSize: "1.1rem",
                        py: 1.2,
                        "&:hover": { bgcolor: "#1a5276" },
                      }}
                    >
                      Submit
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </div>
  );
};

export default LandingPage;
